import type { IdentifyPayload } from "@openpanel/web";

// Structural subset of the better-auth session we need to identify a user.
// Kept local so the pure helpers below stay trivially testable.
export interface IdentifiableSession {
  user: {
    id: string;
    isAnonymous?: boolean | null;
    isOnboarded?: boolean | null;
    createdAt?: string | Date | null;
  };
}

type MaybeSession = IdentifiableSession | null | undefined;

/**
 * Identify only real, signed-in users. The anonymous bootstrap account that
 * onboarding creates on first visit stays unidentified so its events remain
 * anonymous until email verification links it to a real profile.
 */
export function shouldIdentify(session: MaybeSession): session is IdentifiableSession {
  return Boolean(session?.user?.id) && !session?.user?.isAnonymous;
}

/**
 * Session data only — no profile lookup, no PII beyond the user id. Traits are
 * limited to onboarding state and account age so events can be segmented
 * without widening the data OpenPanel holds.
 */
export function identifyPayload(session: IdentifiableSession): IdentifyPayload {
  const { id, isOnboarded, createdAt } = session.user;
  return {
    profileId: id,
    properties: {
      isOnboarded: Boolean(isOnboarded),
      ...(createdAt && {
        createdAt:
          createdAt instanceof Date ? createdAt.toISOString() : String(createdAt),
      }),
    },
  };
}
